import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Head from '@/components/base/Head';
import HeroSection from './components/HeroSection';
import ServicesSection from './components/ServicesSection';
import AboutSection from './components/AboutSection';
import PricingSection from './components/PricingSection';
import TestimonialsSection from './components/TestimonialsSection';
import BookingCTA from './components/BookingCTA';
import ClinicGallery from './components/ClinicGallery';
import ProcessTimeline from './components/ProcessTimeline';
import TrustSection from './components/TrustSection';
import Footer from './components/Footer';
import TreatmentsOverview from './components/DentalServicesGrid';

export default function Home() {
  const { t } = useTranslation();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <>
      <Head
        title={t('seo.home.title')}
        metas={[
          { name: 'description', content: t('seo.home.description') },
          { name: 'keywords', content: t('seo.home.keywords') },
          { name: 'geo.region', content: 'AL' },
          { name: 'geo.placename', content: 'Tirana' },
          { name: 'geo.position', content: '41.3275;19.8187' },
          { property: 'og:title', content: t('seo.home.title') },
          { property: 'og:description', content: t('seo.home.description') },
          { property: 'og:type', content: 'website' },
          { property: 'og:url', content: 'https://estevaclinic.com/' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.home.title') },
          { name: 'twitter:description', content: t('seo.home.description') },
          { name: 'last-modified', content: '2026-06-08' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/' },
        ]}
        scripts={[
          {
            type: 'application/ld+json',
            children: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'MedicalOrganization',
              name: 'Esteva Clinic',
              description: t('seo.home.description'),
              address: { '@type': 'PostalAddress', addressLocality: 'Tirana', addressCountry: 'AL' },
              openingHoursSpecification: [
                { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '19:00' },
                { '@type': 'OpeningHoursSpecification', dayOfWeek: 'Saturday', opens: '10:00', closes: '16:00' },
              ],
            }),
          },
          {
            type: 'application/ld+json',
            children: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://estevaclinic.com/' },
              ],
            }),
          },
        ]}
      />
      <main>
        <Navbar />
        <HeroSection />
        <ServicesSection />
        <TreatmentsOverview />
        <AboutSection />
        <ProcessTimeline />
        <TrustSection />
        <PricingSection />
        <TestimonialsSection />
        <BookingCTA />
        <ClinicGallery />
        {/* SEO Treatment Spectrum */}
        <section className="py-10 md:py-12 px-6 md:px-10 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <p className="text-[10px] md:text-[11px] tracking-[0.2em] uppercase text-foreground-300 font-label mb-2 break-words">
              {t('home.seo_service_areas_label')}
            </p>
            <p className="text-[13px] text-foreground-400 leading-relaxed font-body max-w-5xl">
              {t('seo.home.seo_text')}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {Array.from({ length: 12 }, (_, i) => t(`pub.pill_${i + 1}`)).map((pill) => (
                <span key={pill} className="px-3 py-1 rounded-full bg-background-100 border border-background-200/50 text-[11px] text-foreground-300 font-body whitespace-nowrap">
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}